<template>
  <PlatformProvider class="wc-app h-100" v-if="renderApp && !loading && !restricted">
    <component :is="layout" />
    <WCMainSidebar />
    <WCModalRoot @vue:mounted="handlePushConfig" />
    <WCToastRoot />
    <WCToastRootModern />
    <WCCartDrawerRoot />
    <WCMobileContextMenuRoot v-if="isMobile" />
    <WCSpinnerRoot />
  </PlatformProvider>
  <h1 class="error-container" v-else-if="restricted">
    <FeatureNotAvailable :isMobile="isMobile" />
  </h1>
  <h1 v-else-if="!renderApp">
    {{ $t('operaBlockedVersionWarning') }}
  </h1>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import WCModalRoot from '@/modules/modals/components/WCModalRoot/WCModalRoot.vue';
import WCToastRoot from '@/modules/toasts/components/WCToastRoot/WCToastRoot.vue';
import WCToastRootModern from '@/modules/toasts/components/WCToastRootModern/WCToastRootModern.vue';
import WCMobileContextMenuRoot from '@/modules/mobileContextMenu/components/WCMobileContextMenuRoot/WCMobileContextMenuRoot.vue';
import WCCartDrawerRoot from '@/modules/cart/components/WCCartDrawerRoot/WCCartDrawerRoot.vue';
import WCSpinnerRoot from '@/components/WCSpinnerRoot/WCSpinnerRoot.vue';
import { PlatformMixin } from '@/modules/platform';
import PlatformProvider from '@/modules/platform/components/PlatformProvider.vue';
import EmployeeDashboard from '@/layouts/Employee/EmployeeDashboard.vue';
import FeatureNotAvailable from '@/views/Errors/FeatureNotAvailable.vue';
import { defineComponent } from 'vue';
import axios from 'axios';
import { RESPONSE_STATUS_CODE } from '@/constants/ApiConstants';
import pushNotificationService from '@/services/pushNotifications/pushNotification.service';
import DesktopDefault from './layouts/DesktopDefault.vue';
import Basic from './layouts/Basic.vue';
import AccountDashboard from './layouts/Account/AccountDashboard.vue';
import MobileDefault from './layouts/MobileDefault.vue';
import HeaderlessMobile from './layouts/HeaderlessMobile.vue';
import MobileAccountSubpage from './layouts/Account/MobileAccountSubpage.vue';
import MobileSearchHeader from './layouts/MobileSearchHeader.vue';
import MobileSubpageHeader from './layouts/MobileSubpageHeader.vue';
import MobileScanView from './layouts/MobileScanView.vue';
import PwaPromotionalToastMixin from './mixins/PwaPromotionalToast';
import checkIfUnsupportedOpera from './utils/unsupported-browsers';
import WCMainSidebar from './modules/sidebar/components/WCMainSidebar/WCMainSidebar.vue';

export default defineComponent({
  components: {
    PlatformProvider,
    DesktopDefault,
    MobileDefault,
    Basic,
    WCModalRoot,
    FeatureNotAvailable,
    AccountDashboard,
    EmployeeDashboard,
    WCToastRoot,
    WCToastRootModern,
    WCMobileContextMenuRoot,
    WCCartDrawerRoot,
    HeaderlessMobile,
    MobileAccountSubpage,
    MobileSearchHeader,
    WCMainSidebar,
    MobileSubpageHeader,
    MobileScanView,
    WCSpinnerRoot,
  },
  mixins: [PlatformMixin, PwaPromotionalToastMixin],
  data() {
    return {
      loading: true,
      renderApp: true,
    };
  },
  computed: {
    ...mapGetters({
      getUser: 'user/getUser',
      isEmployee: 'user/isEmployee',
    }),
    layout() {
      const layout = this.$route.meta.layout;
      if (this.isMobile) {
        return layout && layout.mobile ? layout.mobile.name : MobileDefault.name;
      }

      return layout && layout.desktop ? layout.desktop.name : DesktopDefault.name;
    },
    defaultLayout() {
      return this.isMobile ? MobileDefault.name : DesktopDefault.name;
    },
    restricted() {
      const restricted = this.$route.meta.restricted;
      if (
        (restricted && restricted.desktop && !this.isMobile) ||
        (restricted && restricted.mobile && this.isMobile)
      )
        return true;
      return false;
    },
  },
  methods: {
    ...mapActions({
      reload: 'cart/reload',
      fetchLists: 'lists/fetch',
      getCarts: 'cart/getCarts',
    }),
    async attemptToAuthenticateWithRefreshToken() {
      console.log(
        'Unconditionally attempting to authenticate with refresh token ' +
          'because the cookie is HttpOnly',
      );
      await axios
        .post('api/auth/authenticateUsingRefreshToken')
        .then(response => {
          const responseStatus = response.status;
          if (responseStatus === RESPONSE_STATUS_CODE.CODE_ACCEPTED) {
            console.log('Request accepted, no authentication was possible / necessary');
            return;
          }
          console.log('Successfully authenticated');
        })
        .catch(error => {
          const errorStatus = error?.response?.status;
          if (errorStatus === RESPONSE_STATUS_CODE.CODE_RATE_LIMIT) {
            console.error('Failed to authenticate due to rate limit');
            return;
          }
          const errorMessage = error?.response?.data?.message;
          console.warn(`Failed to authenticate: ${errorMessage}`, error);
        });
    },
    handlePushConfig() {
      pushNotificationService
        .handlePushConfigOnUserChange(
          this.getUser?.sub,
          this.isEmployee,
          this.$configuration?.enabledPlatforms,
          this.$configuration?.webPushConfig,
          this.$configuration?.publicVapidKey,
          this.$configuration?.clientTokenRefreshDuration,
          this.$configuration?.pushNotificationServerTokenExpiration,
          this.$route?.name,
        )
        .catch(error => {
          console.error('Error handling push config on user change:', error);
        });
    },
  },
  async created() {
    await this.attemptToAuthenticateWithRefreshToken();
    await this.reload();
    await this.fetchLists();
    this.renderApp = !checkIfUnsupportedOpera();
    this.loading = false;
  },
});
</script>
<style scoped>
.error-container {
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
}
</style>
